import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './GuidedPage.css';
import APP_CONFIG, {
    TIER_ORDER,
    getBillablePlatforms,
    getOptionalFeatures,
    getVideoQualityOptions,
    getCurrencySymbol,
    formatCapacity
} from '../config/appConfig';
import {
    priceQuote,
    buildProjections,
    makeMoneyFormatter,
    DEFAULT_QUOTE_STATE,
    SUBSCRIBER_RATIOS
} from '../lib/pricing';
import ProjectionsChart from '../components/ProjectionsChart';
import ShareQuote from '../components/ShareQuote';
import { useSettings } from '../lib/SettingsContext';

const DRAFT_KEY = 'inorain.guided.draft';

/**
 * Guided discovery flow.
 *
 * The wizard is deliberately question-led: each step leads with the sentence a
 * salesperson would actually say on the call, then explains why the answer
 * matters and what to probe next. The inputs are a consequence of the
 * conversation rather than the other way round, which is what makes this usable
 * live on a call and on a phone.
 */

/** Large tap-target radio cards — no dropdowns on the critical path. */
const OptionCards = ({ options, value, onChange, columns = 1 }) => (
    <div className={`option-cards cols-${columns}`}>
        {options.map(opt => (
            <button
                type="button"
                key={opt.value}
                className={`option-card ${value === opt.value ? 'selected' : ''}`}
                onClick={() => onChange(opt.value)}
                aria-pressed={value === opt.value}
            >
                {opt.icon && <span className="option-icon">{opt.icon}</span>}
                <span className="option-text">
                    <span className="option-label">{opt.label}</span>
                    {opt.hint && <span className="option-hint">{opt.hint}</span>}
                </span>
            </button>
        ))}
    </div>
);

/** Numeric field with thumb-sized steppers for phone use. */
const NumberField = ({ label, value, onChange, unit, step = 1, min = 0, hint }) => (
    <div className="number-field">
        <label>{label}</label>
        <div className="number-control">
            <button type="button" aria-label={`Decrease ${label}`}
                onClick={() => onChange(Math.max(min, Number(value) - step))}>−</button>
            <div className="number-value">
                <input
                    type="number"
                    inputMode="numeric"
                    value={value}
                    min={min}
                    onChange={(e) => onChange(Math.max(min, Number(e.target.value)))}
                />
                {unit && <span className="number-unit">{unit}</span>}
            </div>
            <button type="button" aria-label={`Increase ${label}`}
                onClick={() => onChange(Number(value) + step)}>+</button>
        </div>
        {hint && <p className="field-hint">{hint}</p>}
    </div>
);

const GuidedPage = () => {
    const { effective: calculationSettings, values: settingsValues } = useSettings();
    const [step, setStep] = useState(0);
    const [state, setState] = useState(() => {
        try {
            const saved = localStorage.getItem(DRAFT_KEY);
            if (saved) return { ...DEFAULT_QUOTE_STATE, ...JSON.parse(saved) };
        } catch (e) {
            // A blocked or corrupt store must not stop the tool loading.
        }
        return { ...DEFAULT_QUOTE_STATE };
    });

    // Persist the draft so a call interrupted mid-discovery is not lost.
    useEffect(() => {
        try {
            localStorage.setItem(DRAFT_KEY, JSON.stringify(state));
        } catch (e) {
            // Non-fatal: the wizard still works without a saved draft.
        }
    }, [state]);

    const set = useCallback((patch) => setState(prev => ({ ...prev, ...patch })), []);
    const toggleIn = useCallback((bucket, key) => {
        setState(prev => ({ ...prev, [bucket]: { ...prev[bucket], [key]: !prev[bucket][key] } }));
    }, []);

    const priced = useMemo(() => priceQuote(state, calculationSettings), [state, calculationSettings]);
    const { requirements, revenue, tierCosts, recommendedTier, upgradeInsight, activeSubscribers, usage } = priced;
    const symbol = getCurrencySymbol(state.currency);

    const money = makeMoneyFormatter(state.currency);

    const projections = useMemo(
        () => buildProjections({ revenue, tierCosts, monthlyGrowthRate: state.monthlyGrowthRate }),
        [revenue, tierCosts, state.monthlyGrowthRate]
    );

    const selectedPlatformCount = Object.entries(state.selectedPlatforms)
        .filter(([k, on]) => on && APP_CONFIG.platforms[k] && APP_CONFIG.platforms[k].price > 0).length;
    const selectedFeatureCount = Object.entries(state.selectedFeatures)
        .filter(([k, on]) => on && APP_CONFIG.features[k]).length;

    // ---------------------------------------------------------------------
    // Steps. `ask` is the question to put to the client, verbatim.
    // ---------------------------------------------------------------------
    const steps = [
        {
            id: 'content',
            nav: 'Content',
            ask: 'What kind of content will you be streaming?',
            why: 'Content type sets the concurrent-to-total viewer ratio, which drives both the capacity we quote and the tier you land on.',
            probe: 'Live events need very different infrastructure from on-demand — worth asking whether they plan both.',
            summary: SUBSCRIBER_RATIOS[state.subscriberEstimationType].name,
            body: (
                <>
                    <OptionCards
                        columns={2}
                        value={state.subscriberEstimationType}
                        onChange={(v) => set({ subscriberEstimationType: v })}
                        options={Object.entries(SUBSCRIBER_RATIOS).map(([key, r]) => ({
                            value: key,
                            label: r.name,
                            hint: `${r.description} · 1:${r.ratio} ratio`
                        }))}
                    />
                    <div className="field-block">
                        <label className="field-label">What quality will you stream at?</label>
                        <OptionCards
                            columns={2}
                            value={state.videoQuality}
                            onChange={(v) => set({ videoQuality: v })}
                            options={getVideoQualityOptions().map(o => ({
                                value: o.value,
                                label: o.value,
                                hint: o.label.split('→').slice(1).join('·').trim()
                            }))}
                        />
                        <p className="field-hint">
                            Quality is the single biggest lever on bandwidth. If they are unsure, Mixed is the
                            realistic default for a catalogue watched on both phones and TVs.
                        </p>
                    </div>
                </>
            )
        },
        {
            id: 'catalogue',
            nav: 'Catalogue',
            ask: 'How much content will you launch with?',
            why: 'Asset count and average length give us your storage footprint, including the transcode ladder and backups.',
            probe: 'Ask whether the library grows monthly — that changes which tier they outgrow first.',
            summary: `${Number(state.videoAssets).toLocaleString()} titles · ${formatCapacity(requirements.storage)}`,
            body: (
                <>
                    <NumberField
                        label="Number of video assets"
                        value={state.videoAssets}
                        onChange={(v) => set({ videoAssets: v })}
                        step={50}
                    />
                    <NumberField
                        label="Average video length"
                        value={state.avgDuration}
                        onChange={(v) => set({ avgDuration: v })}
                        unit="min"
                        step={10}
                    />
                    <div className="live-estimate">
                        <span className="live-label">Storage needed</span>
                        <span className="live-value">{formatCapacity(requirements.storage)}</span>
                        <span className="live-note">
                            Includes the {calculationSettings.efficiencyFactors.storageOverhead.value}× overhead
                            for multiple renditions, thumbnails and backups.
                        </span>
                    </div>
                </>
            )
        },
        {
            id: 'audience',
            nav: 'Audience',
            ask: 'How many people will be watching at once, and for how long?',
            why: 'Peak concurrency and session length are what actually determine bandwidth — total signups do not.',
            probe: 'If they only know total subscribers, work backwards: peak concurrency is typically a small fraction of the base.',
            summary: `${Number(state.totalPlays).toLocaleString()} peak · ${activeSubscribers.toLocaleString()} subs`,
            body: (
                <>
                    <NumberField
                        label="Peak concurrent viewers"
                        value={state.totalPlays}
                        onChange={(v) => set({ totalPlays: v })}
                        step={10}
                        hint="How many streams are running at the busiest moment."
                    />
                    <NumberField
                        label="Average watch time per session"
                        value={state.avgWatchTime}
                        onChange={(v) => set({ avgWatchTime: v })}
                        unit="min"
                        step={5}
                    />
                    <div className="live-estimate two-up">
                        <div>
                            <span className="live-label">Approx. active subscribers</span>
                            <span className="live-value">{activeSubscribers.toLocaleString()}</span>
                        </div>
                        <div>
                            <span className="live-label">Bandwidth needed</span>
                            <span className="live-value">{formatCapacity(requirements.bandwidth)}</span>
                        </div>
                        <span className="live-note">
                            Derived from a 1:{SUBSCRIBER_RATIOS[state.subscriberEstimationType].ratio} ratio for
                            {' '}{SUBSCRIBER_RATIOS[state.subscriberEstimationType].name.toLowerCase()}.
                            Active subscribers is the number that decides the tier.
                        </span>
                    </div>
                </>
            )
        },
        {
            id: 'platforms',
            nav: 'Platforms',
            ask: 'Where do your viewers want to watch?',
            why: 'Apps are bundled by tier. Which devices they name decides whether a tier upgrade or individual add-ons is the cheaper route.',
            probe: 'Push for priorities rather than a wishlist — "which two matter at launch?" keeps the quote honest.',
            summary: selectedPlatformCount === 0 ? 'Web only' : `${selectedPlatformCount} + web`,
            body: (
                <>
                    <p className="step-note">
                        The web player is included on every tier. Everything else is{' '}
                        {money(150)}/month, or free once a tier bundles it.
                    </p>
                    <div className="pick-grid">
                        {/* Web is on every tier at no cost, so it is shown ticked and
                            locked rather than omitted — leaving it out made the quote
                            look as though the customer got no web player at all. */}
                        <div className="pick-item selected is-locked">
                            <span className="pick-icon">{APP_CONFIG.platforms.web.icon}</span>
                            <span className="pick-name">{APP_CONFIG.platforms.web.name}</span>
                            <span className="pick-meta">Included in the package</span>
                            <span className="pick-check" aria-hidden="true">✓</span>
                        </div>

                        {getBillablePlatforms().map(([key, platform]) => (
                            <button
                                type="button"
                                key={key}
                                className={`pick-item ${state.selectedPlatforms[key] ? 'selected' : ''}`}
                                onClick={() => toggleIn('selectedPlatforms', key)}
                                aria-pressed={!!state.selectedPlatforms[key]}
                            >
                                <span className="pick-icon">{platform.icon}</span>
                                <span className="pick-name">{platform.name}</span>
                                <span className="pick-meta">
                                    {platform.includedFrom
                                        ? `free from ${APP_CONFIG.packages[platform.includedFrom].name}`
                                        : 'add-on only'}
                                </span>
                                <span className="pick-check" aria-hidden="true">✓</span>
                            </button>
                        ))}
                    </div>
                </>
            )
        },
        {
            id: 'features',
            nav: 'Features',
            ask: 'Which capabilities do you need at launch?',
            why: 'Nine features ship with every tier. These ten are the ones that move the price, so only tick what they will actually use on day one.',
            probe: 'Separate "need at launch" from "want eventually" — the second list is your upsell path, not this quote.',
            summary: selectedFeatureCount === 0 ? 'Core only' : `${selectedFeatureCount} extra`,
            body: (
                <div className="pick-grid">
                    {getOptionalFeatures().map(([key, feature]) => (
                        <button
                            type="button"
                            key={key}
                            className={`pick-item ${state.selectedFeatures[key] ? 'selected' : ''}`}
                            onClick={() => toggleIn('selectedFeatures', key)}
                            aria-pressed={!!state.selectedFeatures[key]}
                        >
                            <span className="pick-icon">{feature.icon}</span>
                            <span className="pick-name">{feature.name}</span>
                            <span className="pick-meta">
                                {money(feature.price)}/mo · free from {APP_CONFIG.packages[feature.includedFrom].name}
                            </span>
                            <span className="pick-check" aria-hidden="true">✓</span>
                        </button>
                    ))}
                </div>
            )
        },
        {
            id: 'usage',
            nav: 'Usage',
            ask: 'Will you run live channels, or need dubbing and subtitles?',
            why: 'These are billed on what gets used rather than a flat fee, so they cost the same whichever tier you pick — and they are easy to leave out of a quote by accident.',
            probe: 'Ask how many hours of content need subtitling at launch, not the whole library — it is usually a much smaller number.',
            summary: usage.total > 0 ? money(usage.total) + '/mo' : 'None',
            body: (
                <>
                    <p className="step-note">
                        Leave these at zero if they are not part of the launch. Channel bandwidth is
                        invoiced after the month, on measured usage.
                    </p>
                    <div className="field-pair">
                        <NumberField
                            label="Live channels (SSAI & Channel Builder)"
                            value={state.ssaiChannels}
                            onChange={(v) => set({ ssaiChannels: v })}
                            unit="ch"
                            hint={`${money(APP_CONFIG.usageAddOns.ssaiChannelBuilder.unitPrice)} per channel / month`}
                        />
                        <NumberField
                            label="Channel bandwidth per month"
                            value={state.ssaiBandwidthTB}
                            onChange={(v) => set({ ssaiBandwidthTB: v })}
                            unit="TB"
                            step={5}
                            hint={`${money(APP_CONFIG.usageAddOns.ssaiChannelBuilder.bandwidthPricePerTB)} / TB, billed separately from your tier`}
                        />
                    </div>
                    <div className="field-pair">
                        <NumberField
                            label="AI dubbing per month"
                            value={state.aiDubbingMinutes}
                            onChange={(v) => set({ aiDubbingMinutes: v })}
                            unit="min"
                            step={30}
                            hint={`$${APP_CONFIG.usageAddOns.aiDubbing.unitPrice.toFixed(2)} / minute`}
                        />
                        <NumberField
                            label="AI subtitles per month"
                            value={state.aiSubtitleMinutes}
                            onChange={(v) => set({ aiSubtitleMinutes: v })}
                            unit="min"
                            step={30}
                            hint={`$${APP_CONFIG.usageAddOns.aiSubtitles.unitPrice.toFixed(2)} / minute`}
                        />
                    </div>
                    {usage.total > 0 && (
                        <div className="live-estimate">
                            <span className="live-label">Usage per month</span>
                            <span className="live-value">{money(usage.total)}</span>
                            <span className="live-note">
                                {usage.items.map(i => `${i.name} (${i.detail})`).join(' · ')}
                            </span>
                        </div>
                    )}
                </>
            )
        },
        {
            id: 'revenue',
            nav: 'Revenue',
            ask: 'How will the service make money?',
            why: 'The revenue model turns the cost into a margin, which is the number that decides whether they sign.',
            probe: 'Ask what they would charge, not what they hope to earn — price per user is far easier for them to answer.',
            summary: `${state.revenueModel} · ${money(revenue)}/mo`,
            body: (
                <>
                    <OptionCards
                        columns={2}
                        value={state.revenueModel}
                        onChange={(v) => set({ revenueModel: v })}
                        options={Object.entries(APP_CONFIG.revenueModels).map(([key, m]) => ({
                            value: key, label: key, hint: m.description
                        }))}
                    />
                    {(state.revenueModel === 'SVOD' || state.revenueModel === 'HYBRID') && (
                        <div className="field-pair">
                            <NumberField label="Paying subscribers" value={state.subscribers}
                                onChange={(v) => set({ subscribers: v })} step={100} />
                            <NumberField label={`Monthly price (${symbol})`} value={state.avgSpend}
                                onChange={(v) => set({ avgSpend: v })} step={1} />
                        </div>
                    )}
                    {(state.revenueModel === 'AVOD' || state.revenueModel === 'HYBRID') && (
                        <div className="field-pair">
                            <NumberField label="Monthly ad impressions" value={state.impressions}
                                onChange={(v) => set({ impressions: v })} step={100000} />
                            <NumberField label={`CPM (${symbol})`} value={state.cpm}
                                onChange={(v) => set({ cpm: v })} step={1} />
                        </div>
                    )}
                    {(state.revenueModel === 'TVOD' || state.revenueModel === 'HYBRID') && (
                        <div className="field-pair">
                            <NumberField label="Transactions per month" value={state.transactions}
                                onChange={(v) => set({ transactions: v })} step={100} />
                            <NumberField label={`Price per view (${symbol})`} value={state.avgPrice}
                                onChange={(v) => set({ avgPrice: v })} step={1} />
                        </div>
                    )}
                    <div className="live-estimate">
                        <span className="live-label">Monthly revenue</span>
                        <span className="live-value">{money(revenue)}</span>
                    </div>
                </>
            )
        },
        {
            id: 'quote',
            nav: 'Quote',
            ask: 'Here is what it costs.',
            why: null,
            probe: null,
            summary: tierCosts[recommendedTier].isCustom ? 'Custom' : money(tierCosts[recommendedTier].monthlyRecurringCost),
            body: null // rendered separately below
        }
    ];

    const current = steps[step];
    const isLast = step === steps.length - 1;
    const progress = ((step + 1) / steps.length) * 100;

    const goTo = (index) => {
        setStep(Math.max(0, Math.min(steps.length - 1, index)));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const resetDraft = () => {
        setState({ ...DEFAULT_QUOTE_STATE });
        setStep(0);
        try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* ignore */ }
    };

    return (
        <div className="guided-page">
            {/* Progress rail: tappable, so a salesperson can jump back mid-call. */}
            <div className="guided-progress">
                <div className="progress-bar"><div className="progress-fill" style={{ width: `${progress}%` }} /></div>
                <ol className="progress-steps">
                    {steps.map((s, i) => (
                        <li key={s.id}>
                            <button
                                type="button"
                                className={`progress-step ${i === step ? 'current' : ''} ${i < step ? 'done' : ''}`}
                                onClick={() => goTo(i)}
                                aria-current={i === step ? 'step' : undefined}
                            >
                                <span className="step-index">{i < step ? '✓' : i + 1}</span>
                                <span className="step-name">{s.nav}</span>
                                {i !== step && i < step && <span className="step-summary">{s.summary}</span>}
                            </button>
                        </li>
                    ))}
                </ol>
            </div>

            <div className="guided-body">
                {!isLast ? (
                    <div className="guided-card">
                        <span className="step-counter">Step {step + 1} of {steps.length}</span>
                        <h1 className="step-ask">{current.ask}</h1>
                        {current.why && (
                            <div className="step-rationale">
                                <p><strong>Why it matters.</strong> {current.why}</p>
                                {current.probe && <p className="step-probe"><strong>Then probe.</strong> {current.probe}</p>}
                            </div>
                        )}
                        <div className="step-inputs">{current.body}</div>
                    </div>
                ) : (
                    <div className="guided-quote">
                        <div className="quote-hero">
                            <span className="quote-eyebrow">Recommended for this customer</span>
                            <h1>{APP_CONFIG.packages[recommendedTier].name}</h1>
                            <p className="quote-price">
                                {tierCosts[recommendedTier].isCustom
                                    ? 'Custom quote'
                                    : <>{money(tierCosts[recommendedTier].monthlyRecurringCost)}<span>/month</span></>}
                            </p>
                            <p className="quote-reason">
                                {tierCosts[recommendedTier].isCustom
                                    ? `An estimated ${activeSubscribers.toLocaleString()} active subscribers is beyond the published tiers, so this needs a custom quote.`
                                    : `Cheapest tier whose guidance covers ${activeSubscribers.toLocaleString()} active subscribers.`}
                            </p>
                            {!tierCosts[recommendedTier].isCustom && (
                                <p className="quote-yearly">
                                    Or {money(tierCosts[recommendedTier].monthlyRecurringCost * 12 * (1 - APP_CONFIG.billing.yearlyDiscount))}/year
                                    — saves {money(tierCosts[recommendedTier].monthlyRecurringCost * 12 * APP_CONFIG.billing.yearlyDiscount)}.
                                </p>
                            )}
                        </div>

                        <div className="quote-breakdown">
                            <h2>What makes up the price</h2>
                            <ul>
                                <li><span>{APP_CONFIG.packages[recommendedTier].name} plan</span>
                                    <b>{tierCosts[recommendedTier].isCustom ? '—' : money(tierCosts[recommendedTier].baseCost)}</b></li>
                                <li><span>Platform add-ons{tierCosts[recommendedTier].platformAddOnItems.length > 0
                                    ? ` (${tierCosts[recommendedTier].platformAddOnItems.map(i => i.name).join(', ')})` : ''}</span>
                                    <b>{tierCosts[recommendedTier].platformAddOnCost > 0
                                        ? money(tierCosts[recommendedTier].platformAddOnCost) : 'included'}</b></li>
                                <li><span>Feature add-ons{tierCosts[recommendedTier].featureAddOnItems.length > 0
                                    ? ` (${tierCosts[recommendedTier].featureAddOnItems.map(i => i.name).join(', ')})` : ''}</span>
                                    <b>{tierCosts[recommendedTier].featureAddOnCost > 0
                                        ? money(tierCosts[recommendedTier].featureAddOnCost) : 'included'}</b></li>
                                {tierCosts[recommendedTier].usageItems.map(item => (
                                    <li key={item.key}>
                                        <span>{item.name} ({item.detail})</span>
                                        <b>{money(item.cost)}</b>
                                    </li>
                                ))}
                                <li><span>Capacity overage</span>
                                    <b>{tierCosts[recommendedTier].overageCost > 0
                                        ? money(tierCosts[recommendedTier].overageCost) : 'none'}</b></li>
                            </ul>
                            {upgradeInsight.kind === 'stack-wins' && (
                                <p className="quote-flag">
                                    Worth knowing: {APP_CONFIG.packages[upgradeInsight.cheapest].name} with{' '}
                                    {upgradeInsight.addOnCount} add-on{upgradeInsight.addOnCount === 1 ? '' : 's'} costs{' '}
                                    {money(upgradeInsight.delta)}/month less than{' '}
                                    {APP_CONFIG.packages[upgradeInsight.fullyBundled].name}, which bundles them — but check
                                    the capacity guidance before quoting it.
                                </p>
                            )}
                            {upgradeInsight.kind === 'upgrade-wins' && (
                                <p className="quote-flag good">
                                    {APP_CONFIG.packages[upgradeInsight.fullyBundled].name} bundles all{' '}
                                    {upgradeInsight.addOnCount} add-ons and is {money(upgradeInsight.delta)}/month
                                    cheaper than stacking them on {APP_CONFIG.packages[upgradeInsight.cheapest].name}.
                                </p>
                            )}
                        </div>

                        {/* Compact tier comparison — scrolls horizontally on a phone. */}
                        <div className="quote-compare">
                            <h2>All tiers</h2>
                            <div className="compare-scroll">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Tier</th><th>Bandwidth</th><th>Storage</th><th>Subscribers</th><th>Total / mo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {TIER_ORDER.map(t => (
                                            <tr key={t} className={t === recommendedTier ? 'is-recommended' : ''}>
                                                <th scope="row">
                                                    {APP_CONFIG.packages[t].name}
                                                    {t === recommendedTier && <span className="row-badge">Best fit</span>}
                                                </th>
                                                <td>{formatCapacity(APP_CONFIG.packages[t].bandwidth)}</td>
                                                <td>{formatCapacity(APP_CONFIG.packages[t].storage)}</td>
                                                <td>{APP_CONFIG.packages[t].activeSubscribers === Infinity
                                                    ? 'Custom' : `~${APP_CONFIG.packages[t].activeSubscribers.toLocaleString()}`}</td>
                                                <td className="num">{tierCosts[t].isCustom
                                                    ? 'Contact us' : money(tierCosts[t].monthlyRecurringCost)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <ProjectionsChart
                            projections={projections}
                            currencySymbol={symbol}
                            monthlyGrowthRate={state.monthlyGrowthRate}
                        />

                        <ShareQuote quoteState={state} priced={priced} settingsValues={settingsValues} />

                        <button type="button" className="reset-link" onClick={resetDraft}>
                            Start a new quote
                        </button>
                    </div>
                )}
            </div>

            {/* Thumb-reachable, always-visible step nav. */}
            <div className="guided-nav">
                <button type="button" className="nav-back" onClick={() => goTo(step - 1)} disabled={step === 0}>
                    Back
                </button>
                <div className="nav-running">
                    {!isLast && (
                        <>
                            <span>{tierCosts[recommendedTier].isCustom
                                ? 'Custom' : money(tierCosts[recommendedTier].monthlyRecurringCost)}</span>
                            <small>{APP_CONFIG.packages[recommendedTier].name} so far</small>
                        </>
                    )}
                </div>
                {!isLast ? (
                    <button type="button" className="nav-next" onClick={() => goTo(step + 1)}>
                        {step === steps.length - 2 ? 'See the quote' : 'Next'}
                    </button>
                ) : (
                    <button type="button" className="nav-next" onClick={() => goTo(0)}>
                        Edit answers
                    </button>
                )}
            </div>
        </div>
    );
};

export default GuidedPage;
